<template>
  <v-container>
    <v-radio-group :disabled="isEdit" v-model="isCompany" row data-test-form-isCompany>
      <v-radio :label="$t('report.thgContactData.corporateClient')" :value="true" data-test-form-iscompany></v-radio>
      <v-radio :label="$t('report.thgContactData.private')" :value="false" data-test-form-isprivate></v-radio>
    </v-radio-group>
    <v-form v-if="isCompany" v-model="valid" ref="chargingStationForm">
      <v-card flat :loading="loading">
        <v-text-field
          v-model="chargingStation.name"
          :rules="requiredRule"
          :label="$t('components.thg.ThgChargingStationForm.name')"
          :hint="$t('components.thg.ThgChargingStationForm.nameHint')"
          required
          outlined
          data-test-form-name
        ></v-text-field>
        <v-card-text>
          <h3>
            {{ $t("components.thg.ThgChargingStationForm.chargingStationOperaterTitle") }}
          </h3>
        </v-card-text>
        <v-text-field
          v-model="chargingStation.chargingPointOperator.name"
          :label="$t(`components.thg.ThgChargingStationForm.${countryCode}.chargingpointOperaterName`)"
          :hint="$t(`components.thg.ThgChargingStationForm.${countryCode}.chargingpointOperaterNameHint`)"
          :rules="requiredRule"
          :disabled="isEdit"
          required
          outlined
          data-test-form-company
        ></v-text-field>
        <v-text-field
          v-if="isBnetzA"
          v-model="chargingStation.chargingPointOperator.bnetzaOperatorId"
          :label="$t(`components.thg.ThgChargingStationForm.${countryCode}.bnetzaOperatorId`)"
          :hint="$t(`components.thg.ThgChargingStationForm.${countryCode}.bnetzaOperatorIdHint`)"
          persistent-hint
          :rules="bnetzaOperatorIdRule"
          :disabled="isEdit"
          required
          outlined
          data-test-form-bnetza-operator-id
        ></v-text-field>

        <v-card-text>
          <h3>
            {{ $t("components.thg.ThgChargingStationForm.chargingStationLocationTitle") }}
          </h3>
        </v-card-text>
        <v-text-field
          v-model="chargingStation.address.street"
          :disabled="isEdit"
          :rules="requiredRule"
          :label="$t('components.thg.ThgChargingStationForm.street')"
          :hint="$t('components.thg.ThgChargingStationForm.addressHint')"
          required
          outlined
          data-test-form-street
        ></v-text-field>
        <v-text-field
          v-model="chargingStation.address.zip"
          :disabled="isEdit"
          :rules="germanZipRule"
          :label="$t('components.thg.ThgChargingStationForm.zip')"
          :hint="$t('components.thg.ThgChargingStationForm.addressHint')"
          required
          outlined
          data-test-form-zip
        ></v-text-field>
        <v-text-field
          v-model="chargingStation.address.city"
          :disabled="isEdit"
          :rules="requiredRule"
          :label="$t('components.thg.ThgChargingStationForm.city')"
          :hint="$t('components.thg.ThgChargingStationForm.addressHint')"
          required
          outlined
          data-test-form-city
        ></v-text-field>

        <v-autocomplete
          v-if="detail"
          v-model="chargingStation.address.countryCode"
          :items="CountryCodes"
          :disabled="isEdit"
          :rules="requiredRule"
          :label="$t('components.thg.ThgChargingStationForm.countryCode')"
          :hint="$t('components.thg.ThgChargingStationForm.countryCodeHint')"
          required
          outlined
          data-test-form-country-code
        >
          <template #item="{ item }">
            {{ $t(`CountryCodeEnum.${item}`) }}
          </template>
          <template #selection="{ item }">
            {{ $t(`CountryCodeEnum.${item}`) }}
          </template>
        </v-autocomplete>

        <geo-finder-field
          v-if="detail"
          :address.sync="chargingStation.address"
          :value.sync="chargingStation.address.geo.lat"
          type="lat"
          :label="$t('components.thg.ThgChargingStationForm.geoLat')"
          :hint="$t('components.thg.ThgChargingStationForm.geoLatHint')"
          :disabled="isEdit"
          :enforce-geo-bounds="true"
          data-test-form-lat
        />

        <geo-finder-field
          v-if="detail"
          :address.sync="chargingStation.address"
          :value.sync="chargingStation.address.geo.lng"
          type="lng"
          :label="$t('components.thg.ThgChargingStationForm.geoLng')"
          :hint="$t('components.thg.ThgChargingStationForm.geoLngHint')"
          :disabled="isEdit"
          :enforce-geo-bounds="true"
          data-test-form-lng
        />

        <v-card-text>
          <h3>
            {{ $t("components.thg.ThgChargingStationForm.chargingStationData") }}
          </h3>
        </v-card-text>

        <v-slider
          class="mt-8 mb-4 align-center"
          v-model.number="chargingStation.chargingPointCount"
          :label="$t('components.thg.ThgChargingStationForm.chargingPointCount')"
          :hint="chargingPointAmountHint"
          persistent-hint
          :rules="chargingPointNumberRule"
          :min="minChargingPointAmount"
          :max="maxChargingPointAmount"
          :disabled="isEdit"
          :color="chargingPointAmountSliderColor"
          type="number"
          thumb-label="always"
        >
          <template v-slot:append>
            <v-text-field
              v-model="chargingStation.chargingPointCount"
              :rules="chargingPointNumberRule"
              class="mt-0 pt-0"
              type="number"
              :min="minChargingPointAmount"
              :max="maxChargingPointAmount"
              :disabled="isEdit"
              style="width: 80px"
              outlined
              data-test-form-charging-point-count
            ></v-text-field> </template
        ></v-slider>

        <v-text-field
          v-if="isEvseEnabled"
          v-model="chargingStation.chargingPointOperator.evseOperatorId"
          :label="$t('components.thg.ThgChargingStationForm.evseOperatorId')"
          :hint="$t('components.thg.ThgChargingStationForm.evseOperatorIdHint')"
          :disabled="isEdit"
          persistent-hint
          required
          outlined
          data-test-form-evseId-id
          minlength="3"
          maxlength="3"
        ></v-text-field>

        <div v-if="chargingStation.chargingPointOperator.evseOperatorId">
          <v-text-field
            v-for="i in chargingStation.chargingPointCount"
            :key="i"
            ref="powerOutletId"
            v-model="powerOutletId[i - 1]"
            :rules="evseOutletRule"
            :label="$t('components.thg.ThgChargingStationForm.evseOutletId')"
            :hint="$t('components.thg.ThgChargingStationForm.evseOutletIdHint')"
            :disabled="isEdit"
            minlength="1"
            maxlength="30"
            autocomplete="off"
            type="text"
            outlined
            :prefix="evseId"
            data-test-form-evse-power-outlet-id
          ></v-text-field>
        </div>

        <v-text-field
          class="pt-4"
          v-model.number="chargingStation.connectionPowerInKwh"
          :rules="connectionPowerRule"
          :min="minConnectionPower"
          :label="$t(`components.thg.ThgChargingStationForm.${countryCode}.connectionPowerInKwh`)"
          :hint="$t(`components.thg.ThgChargingStationForm.${countryCode}.connectionPowerInKwhHint`)"
          :disabled="isEdit"
          required
          outlined
          type="number"
          thumb-label="always"
          suffix="kW"
          data-test-form-connection-power
        ></v-text-field>

        <v-text-field
          v-model="commissioningDate"
          type="date"
          :rules="requiredRule"
          :label="$t('components.thg.ThgChargingStationForm.commissioningDate')"
          :hint="$t('components.thg.ThgChargingStationForm.commissioningDateHint')"
          :disabled="isEdit"
          outlined
          persistent-hint
          data-test-form-commissioningDate
          @blur="chargingStation.commissioningDate = $event.target.value"
        ></v-text-field>

        <v-card-text class="px-0 pb-0">
          <p>{{ $t(`components.thg.ThgChargingStationForm.${countryCode}.registration`) }}</p>
        </v-card-text>

        <v-select
          class="mb-8 pt-4"
          v-model="chargingStation.isRegistered"
          :items="isRegisteredItems"
          persistent-hint
          :label="$t(`components.thg.ThgChargingStationForm.${countryCode}.isRegistered`)"
          :hint="$t(`components.thg.ThgChargingStationForm.${countryCode}.isRegisteredHint`)"
          :disabled="isEdit"
          outlined
          data-test-form-registered
        ></v-select>

        <v-text-field
          v-if="chargingStation.isRegistered"
          v-model="registrationDate"
          type="date"
          :rules="requiredRule"
          :label="$t(`components.thg.ThgChargingStationForm.${countryCode}.registrationDate`)"
          :hint="$t(`components.thg.ThgChargingStationForm.${countryCode}.registrationDateHint`)"
          :disabled="isEdit"
          outlined
          persistent-hint
          data-test-form-registrationDate
          @blur="chargingStation.registrationDate = $event.target.value"
        ></v-text-field>

        <v-card-text class="px-0 pb-0">
          <p>{{ $t(`components.thg.ThgChargingStationForm.renewableEnergy`) }}</p>
        </v-card-text>

        <v-select
          class="mb-8 pt-4"
          v-model="chargingStation.isFromRenewableEnergy"
          :items="isRenewableEnergyItems"
          persistent-hint
          :label="$t(`components.thg.ThgChargingStationForm.${countryCode}.isRenewableEnergy`)"
          :hint="$t(`components.thg.ThgChargingStationForm.${countryCode}.isRenewableEnergyHint`)"
          :disabled="isEdit"
          outlined
          data-test-form-renewable-energy
        ></v-select>

        <!-- Charging station Proofs -->
        <div v-if="chargingStation.isFromRenewableEnergy && isRenewableChargingStationEnabled">
          <v-textarea
            v-if="chargingStation.isFromRenewableEnergy"
            :rules="requiredRule"
            :disabled="isEdit"
            v-model="chargingStation.renewableEnergySourceDescription"
            outlined
            persistent-hint
            :label="$t(`components.thg.ThgChargingStationForm.${countryCode}.renewableEnergyDescription`)"
            :hint="$t(`components.thg.ThgChargingStationForm.${countryCode}.renewableEnergyDescriptionHint`)"
          />
        </div>

        <v-btn class="mr-4" color="primary" :disabled="!valid" @click="save" data-test-form-submit>
          {{ $t("components.thg.ThgChargingStationForm.submit") }}
        </v-btn>
      </v-card>
    </v-form>
    <v-card flat>
      <v-card-text v-if="!isCompany">
        <p class="" v-html="$t('components.thg.ThgChargingStationForm.isPrivatePerson')"></p>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script lang="ts">
import { HttpException } from "@/lib/exceptions/http";
import { germanZipRule } from "@/lib/rules/contactRule";
import { beforeCurrentDateRule, dateRule } from "@/lib/rules/dateRule";
import { evseOutletRule } from "@/lib/rules/evseRule";
import { isNumberRule } from "@/lib/rules/isNumberRule";
import { numberHigherRule } from "@/lib/rules/numberHigherRule";
import { numberLowerRule } from "@/lib/rules/numberLowerRule";
import { requiredRule } from "@/lib/rules/requiredRule";
import partnerService from "@/services/shared/partnerService";
import {
  ThgChargingStationDocumentTypeDtoGen,
  ThgChargingStationProofDocumentDtoGen,
  ThgCreateChargingStationDtoGen
} from "@/services/thg/v1/data-contracts";
import { ChargingPointConnector, ChargingPointType } from "@/store/models/thg/create-charging-point";
import { ChargingStationUiDto } from "@/store/models/thg/create-charging-station";
import { FeatureModule } from "@/store/modules/feature.store";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import ThgChargingStationProof from "./ThgChargingStationProof.vue";
import ThgChargingStationProofDocumentTable from "./ThgChargingStationProofDocumentTable.vue";
import { PartnerModule } from "@/store/modules/partner";
import { UserModule } from "@/store/modules/me-user.store";
import { CountryCodeEnum } from "@/lib/enum/country-code.enum";
import { PartnerColor } from "@/lib/partnerColor";
import GeoFinderField from "@/components/utility/GeoFinderField.vue";

@Component({
  components: {
    ThgChargingStationProof,
    ThgChargingStationProofDocumentTable,
    GeoFinderField
  }
})
export default class ThgChargingStationForm extends Vue {
  readonly CountryCodes = Object.values(CountryCodeEnum);

  /**
   * Property that can pass an existing station or create a new empty one.
   */
  @Prop()
  value!: ThgCreateChargingStationDtoGen;

  @Prop({ default: false })
  isEdit!: boolean;

  chargingStation = new ChargingStationUiDto(this.value, this.user, this.countryCode);

  /**
   * Enables certain form properties, e.g. disables the geo location for end users, but enables this for the admin components.
   */
  @Prop({ type: Boolean, default: false })
  detail!: boolean;

  /**
   * Distinguish between submission for an individual or a company.
   * Only allow company submissions for the moment
   */
  isCompany = true;

  /**
   * files added by the user as proof
   */
  files: ThgChargingStationProofDocumentDtoGen[] = [];

  /**
   * Form settings
   */
  minChargingPointAmount = 1;
  maxChargingPointAmount = 8;
  recommendedMaxChargingPointAmount = 4;
  minConnectionPower = 1;

  /**
   * Internal state for the form element to enable submission on complete.
   */
  valid = false;

  /**
   * Internal loading state
   */
  loading = false;

  powerOutletId = [];

  partnerColor = new PartnerColor();

  get partner() {
    return PartnerModule.partner;
  }

  get user() {
    return UserModule.user;
  }

  get isEvseEnabled() {
    return FeatureModule.isEvseEnabled;
  }

  get countryCode(): string {
    if (!this.value || !this.value.address) {
      return this.partner.countryCode;
    }

    return this.value.address.countryCode || this.partner.countryCode || CountryCodeEnum.germany;
  }

  get idType() {
    return "E";
  }

  get evseId() {
    if (!this.chargingStation.address || !this.chargingStation.chargingPointOperator) {
      return "";
    }

    return `${this.chargingStation.address.countryCode}*${this.chargingStation.chargingPointOperator.evseOperatorId}*${this.idType}`;
  }

  get isRenewableChargingStationEnabled() {
    return FeatureModule.isRenewableChargingStationEnabled;
  }

  /**
   * Return the date in the format YYYY-MM-DD so the vuetify component can handle the input
   */
  get commissioningDate() {
    if (!this.chargingStation.commissioningDate) {
      return "";
    }

    return this.chargingStation.commissioningDate.substring(0, 10);
  }

  /**
   * Convert the string returned from the component to a date and store as iso string expected by backend.
   * On invalid date the conversion should fail and date is empty, which results in the backend to fail.
   */
  set commissioningDate(dateString: string) {
    const date = new Date(dateString);
    if (!date) {
      return;
    }

    this.chargingStation.commissioningDate = date.toISOString();
  }

  /**
   * Return the date in the format YYYY-MM-DD so the vuetify component can handle the input
   */
  get registrationDate() {
    if (!this.chargingStation.registrationDate) {
      return "";
    }

    return this.chargingStation.registrationDate.substring(0, 10);
  }

  /**
   * Convert the string returned from the component to a date and store as iso string expected by backend.
   * On invalid date the conversion should fail and date is empty, which results in the backend to fail.
   */
  set registrationDate(dateString: string) {
    const date = new Date(dateString);
    if (!date) {
      return;
    }

    this.chargingStation.registrationDate = date.toISOString();
  }

  get isRenewableEnergyItems() {
    return [
      { text: this.$t("components.thg.ThgChargingStationForm.isRenewable.true"), value: true },
      { text: this.$t("components.thg.ThgChargingStationForm.isRenewable.false"), value: false }
    ];
  }

  get isRegisteredItems() {
    return [
      { text: this.$t("components.thg.ThgChargingStationForm.isCompany.true"), value: true },
      { text: this.$t("components.thg.ThgChargingStationForm.isCompany.false"), value: false }
    ];
  }

  get germanZipRule() {
    return [germanZipRule(), requiredRule()];
  }

  get requiredRule() {
    return [requiredRule()];
  }

  get evseOutletRule() {
    return [evseOutletRule()];
  }

  /**
   *
   * Bnetza Operator ID can contains numbers and letters since syntax changes
   *
   */
  get bnetzaOperatorIdRule() {
    return [requiredRule()];
  }

  get dateRule() {
    return [dateRule(), beforeCurrentDateRule()];
  }

  get numberRule() {
    return [isNumberRule(), requiredRule()];
  }

  get chargingPointNumberRule() {
    return [
      isNumberRule(),
      requiredRule(),
      numberHigherRule(this.minChargingPointAmount),
      numberLowerRule(this.maxChargingPointAmount)
    ];
  }

  get chargingPointAmountHint() {
    return this.chargingStation.chargingPointCount > this.recommendedMaxChargingPointAmount
      ? this.$t("components.thg.ThgChargingStationForm.chargingPointCountHintRecommended")
      : this.$t("components.thg.ThgChargingStationForm.chargingPointCountHint");
  }

  get chargingPointAmountSliderColor() {
    return this.chargingStation.chargingPointCount > this.recommendedMaxChargingPointAmount
      ? this.partnerColor.secondaryDarken_1
      : this.partnerColor.primary;
  }

  get connectionPowerRule() {
    return [isNumberRule(), requiredRule(), numberHigherRule(this.minConnectionPower)];
  }

  get chargingPointTypes() {
    return ChargingPointType;
  }

  get chargingPointConnector() {
    return ChargingPointConnector;
  }

  get isBnetzA() {
    return this.chargingStation.address.countryCode === "DE";
  }

  @Watch("chargingStation.isFromRenewableEnergy")
  clearFiles() {
    /** Reset files and description when clicked back to false */
    if (!this.chargingStation.isFromRenewableEnergy) {
      this.chargingStation.renewableEnergySourceDescription = "";
      this.files = [];
    }
  }

  onFileUpload(file: ThgChargingStationProofDocumentDtoGen) {
    this.files.push(file);
  }

  onRenewableDescriptionChange(description: string) {
    this.chargingStation.renewableEnergySourceDescription = description;
  }

  /**
   * Emits a save event passing the updated property to the parent.
   */
  save() {
    this.chargingStation.chargingPoints = [];
    if (this.isEvseEnabled) {
      for (let x = 0; x < this.chargingStation.chargingPointCount; x++) {
        this.$log.debug(`Adding evseId ${this.powerOutletId[x]}`);
        this.chargingStation.chargingPoints[x] = { evseId: this.powerOutletId[x] };
      }
    }

    this.chargingStation.refs = this.files.map((file) => {
      return {
        refType: file.extensionType,
        refId: file.id
      } as ThgChargingStationDocumentTypeDtoGen;
    });

    this.$log.debug(this.chargingStation);
    this.$emit("save", this.chargingStation);
  }
  // TODO: Used in another component
  async getLocation() {
    this.loading = true;
    const searchstring = `${this.chargingStation.address.street} ${this.chargingStation.address.zip} ${this.chargingStation.address.city} ${this.chargingStation.address.state}`;
    this.$log.warn(searchstring);
    try {
      const searchstring = `${this.chargingStation.address.street} ${this.chargingStation.address.zip} ${this.chargingStation.address.city} ${this.chargingStation.address.state}`;

      const geoResult = await partnerService.geocodehelper(searchstring);

      this.$log.warn(geoResult);

      if (geoResult && geoResult.type != "Point") {
        this.$toast.error("Error fetching geo");
        return;
      }

      this.chargingStation.address.geo.lat = geoResult.coordinates[1];
      this.chargingStation.address.geo.lng = geoResult.coordinates[0];
    } catch (error) {
      this.$log.error(error);
      if (error instanceof HttpException) {
        this.$toast.error(error.message);
      }
    } finally {
      this.loading = false;
    }
  }
}
</script>
